import React, { useState } from 'react';
import { ArrowLeft, Phone, Mail, Clock, MessageCircle } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import WhatsAppButton from '../../components/WhatsAppButton';
import ContactFormRino from '../../components/ContactFormRino';
import { useNavigate } from 'react-router-dom';
import { useLanguage, getPageTexts } from '../../i18n/pageTranslations';

const Contacto = () => {
  const navigate = useNavigate();
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const lang = useLanguage();
  const t = getPageTexts('rinoContacto', lang);

  const handleFormSuccess = () => {
    setShowSuccessMessage(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Breadcrumb */}
      <div className="pt-24 pb-4 bg-cream">
        <div className="container mx-auto px-4">
          <button 
            onClick={() => navigate('/rinomodelacion')}
            className="flex items-center gap-2 text-gold hover:text-gold transition-colors"
          >
            <ArrowLeft size={20} />
            <span>{t.back}</span>
          </button>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 px-4 bg-gradient-to-b from-cream to-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h1 className="text-4xl md:text-5xl font-light text-[#1C1C1A] mb-6">
            {t.title}
          </h1>
          <p className="text-xl text-text-secondary">
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Quick Contact Info */}
      <section className="py-8 px-4 bg-ivory">
        <div className="container mx-auto max-w-5xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <Card className="p-6 text-center hover:shadow-xl transition-all border-none bg-cream rounded-none">
              <div className="w-14 h-14 bg-gold/10 rounded-none flex items-center justify-center mx-auto mb-3">
                <Phone className="text-gold" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1A] mb-1">{t.phone}</h3>
              <a href="tel:952764547" className="text-gold hover:text-gold font-medium">
                952 76 45 47
              </a>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-all border-none bg-cream rounded-none">
              <div className="w-14 h-14 bg-gold100 rounded-none flex items-center justify-center mx-auto mb-3">
                <MessageCircle className="text-gold600" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1A] mb-1">{t.whatsapp}</h3>
              <a 
                href="https://wa.me/34696345792" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-gold600 hover:text-gold700 font-medium"
              >
                696 345 792
              </a>
            </Card>

            <Card className="p-6 text-center hover:shadow-xl transition-all border-none bg-cream rounded-none">
              <div className="w-14 h-14 bg-gold/10 rounded-none flex items-center justify-center mx-auto mb-3">
                <Mail className="text-gold" size={28} />
              </div>
              <h3 className="text-lg font-semibold text-[#1C1C1A] mb-1">{t.email}</h3>
              <a href="mailto:info@rinomodelacion.com" className="text-gold hover:text-gold font-medium">
                info@rinomodelacion.com
              </a>
            </Card>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-12 px-4 bg-cream">
        <div className="container mx-auto max-w-5xl">
          <div className="bg-ivory p-8 rounded-none shadow-lg">
            <h2 className="text-3xl font-light text-[#1C1C1A] mb-2 text-center">
              {t.formTitle}
            </h2>
            <p className="text-text-secondary text-center mb-8">
              {t.formSubtitle}
            </p>
            
            <ContactFormRino onSuccess={handleFormSuccess} />
          </div>
        </div>
      </section>

      {/* Schedule */}
      <section className="py-8 px-4 bg-ivory">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-cream p-8 rounded-none">
            <div className="flex items-center justify-center gap-4 mb-6">
              <Clock className="text-gold" size={32} />
              <h3 className="text-2xl font-light text-[#1C1C1A]">{t.scheduleTitle}</h3>
            </div>
            <div className="text-center text-text-secondary">
              <p className="mb-2"><strong>{t.weekdays}:</strong> {t.weekdaysHours}</p>
              <p><strong>{t.saturday}:</strong> {t.saturdayHours}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Alternative Contact CTA */}
      <section className="py-12 px-4 bg-cream">
        <div className="container mx-auto max-w-3xl">
          <div className="bg-gradient-to-r from-[#1C1C1A] to-[#1C1C1A] p-8 rounded-none text-white text-center">
            <h3 className="text-2xl font-light mb-4">{t.ctaTitle}</h3>
            <p className="text-white/80 mb-6">
              {t.ctaDesc}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={() => window.open('tel:952764547', '_self')}
                className="bg-gold hover:bg-gold text-white px-6 py-3"
              >
                <Phone className="mr-2" size={20} />
                {t.callNow}
              </Button>
              <Button 
                onClick={() => window.open('https://wa.me/34696345792', '_blank')}
                className="bg-gold500 hover:bg-gold600 text-white px-6 py-3"
              >
                <MessageCircle className="mr-2" size={20} />
                WhatsApp
              </Button>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default Contacto;
